import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  IconButton, 
  Tooltip, 
  Typography, 
  Paper,
  Fade,
  Popper,
  ClickAwayListener
} from '@mui/material';
import { FaInfoCircle, FaQuestionCircle } from 'react-icons/fa';

/**
 * A reusable component for displaying help information on hover or click
 * 
 * @param {Object} props - The component props
 * @param {string} props.fieldName - The name of the field
 * @param {string} props.description - The description text to display
 * @param {Object} [props.examples] - Optional examples for the field
 * @param {string} [props.placement] - Tooltip placement (default: "right")
 * @param {string} [props.variant] - 'tooltip' for hover only, 'popper' for click interaction (default: "tooltip")
 * @param {Object} [props.sx] - Additional styles for the icon wrapper
 * @returns {React.ReactElement}
 */
const FieldInfoTooltip = ({ 
  fieldName, 
  description, 
  examples = null,
  placement = 'right',
  variant = 'tooltip',
  sx = {}
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  // Display examples if provided
  const examplesList = examples ? (
    <Box mt={1}>
      <Typography variant="subtitle2" fontWeight={600}>Examples:</Typography>
      {Array.isArray(examples) ? (
        <ul style={{ margin: 0, paddingLeft: '1rem' }}>
          {examples.map((example, index) => (
            <li key={index}>
              <Typography variant="body2">{example}</Typography>
            </li>
          ))}
        </ul>
      ) : (
        <Typography variant="body2">{examples}</Typography>
      )}
    </Box>
  ) : null;

  // Content to display in tooltip or popper
  const content = (
    <>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {fieldName}
      </Typography>
      <Typography variant="body2">
        {description}
      </Typography>
      {examplesList}
    </>
  );
  
  // Simple tooltip for hover interactions
  if (variant === 'tooltip') {
    return (
      <Box sx={{ 
        display: 'inline-flex', 
        alignItems: 'center',
        ml: 0.5,
        ...sx
      }}>
        <Tooltip 
          title={
            <Box p={1} maxWidth={300}>
              {content}
            </Box>
          }
          placement={placement}
          arrow
        >
          <FaInfoCircle 
            size={16}
            style={{ 
              opacity: 0.6,
              color: 'rgba(0, 0, 0, 0.54)',
              '&:hover': {
                opacity: 1,
                color: '#1976d2'
              }
            }} 
          />
        </Tooltip>
      </Box>
    );
  }
  
  // Interactive popper for click interactions
  return (
    <Box sx={{ 
      display: 'inline-flex', 
      alignItems: 'center',
      ml: 0.5,
      ...sx
    }}>
      <IconButton 
        size="small" 
        onClick={handleClick}
        sx={{ 
          p: 0.2,
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'primary.main'
          } 
        }}
      >
        <FaQuestionCircle 
          size={16}
          style={{ 
            opacity: open ? 1 : 0.6,
            color: open ? '#1976d2' : 'rgba(0, 0, 0, 0.54)'
          }}
        />
      </IconButton>
      
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        transition
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper 
              elevation={3}
              sx={{ 
                p: 2, 
                maxWidth: 350,
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {content}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};

FieldInfoTooltip.propTypes = {
  fieldName: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  examples: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  placement: PropTypes.string,
  variant: PropTypes.oneOf(['tooltip', 'popper']),
  sx: PropTypes.object
};

export default FieldInfoTooltip;